import React, { useCallback, useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import { getDownloadHits } from "../services/userService";

// Curiouzz QR (founder 2026-09-04). One smart link, curiouzz.com/get, detects
// the visitor's device and sends them to the right store while recording the
// ?src= channel. This screen is the payoff: how many people each channel
// actually sent to a store, in our own data rather than a third party's.
//
// Read-only. Counts are hits on the link, not installs: the App Store gives
// nobody an install referrer, so an iOS install cannot be traced back to a
// scan. Android carries the referrer through, so Play Console can close that
// loop. The screen says so rather than letting the number be misread.
const CHANNELS = [
  { src: "card", label: "Business card" },
  { src: "wbn", label: "WBN partner" },
  { src: "meta", label: "Meta ads" },
  { src: "press", label: "Press" },
];

const DownloadHitsLayer = () => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState("");

  const load = useCallback(async (d) => {
    setLoading(true);
    setErr("");
    try {
      const res = await getDownloadHits({ days: d });
      setData(res?.data || null);
    } catch (e) {
      setErr("Could not load the QR scan counts.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(days);
  }, [days, load]);

  const copy = (text, key) => {
    try {
      navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(""), 1600);
    } catch (e) {
      /* clipboard blocked; the link is visible to select by hand */
    }
  };

  const rows = data?.bySrc || [];
  const byPlatform = data?.byPlatform || {};
  const total = data?.total || 0;

  const stat = (label, value, icon) => (
    <div className="col-sm-3 col-6">
      <div className="p-16 radius-8 bg-neutral-50 h-100">
        <div className="d-flex align-items-center gap-2 mb-1">
          <Icon icon={icon} className="text-secondary-light" />
          <span className="text-sm text-secondary-light">{label}</span>
        </div>
        <h5 className="mb-0 fw-semibold">{value}</h5>
      </div>
    </div>
  );

  return (
    <div className="card h-100 p-0 radius-12">
      <div className="card-header border-bottom bg-base py-16 px-24 cz-toolbar">
        <div className="cz-topline">
          <div>
            <h6 className="mb-1 fw-semibold">Curiouzz QR</h6>
            <p className="text-sm text-secondary-light mb-0" style={{ maxWidth: 660 }}>
              One smart link sends every visitor to the right store and records
              which channel they came from. Scan the card, click an ad, follow a
              partner link: each one is counted here.
            </p>
          </div>
          <div className="cz-actions">
            <span className="text-sm text-secondary-light">Last</span>
            <select
              className="form-select"
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
            >
              <option value={7}>7 days</option>
              <option value={30}>30 days</option>
              <option value={90}>90 days</option>
              <option value={365}>12 months</option>
            </select>
            <button
              type="button"
              className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1"
              onClick={() => load(days)}
              disabled={loading}
            >
              <Icon icon="mdi:refresh" /> Refresh
            </button>
          </div>
        </div>
      </div>

      <div className="card-body p-24">
        {err ? <div className="alert alert-danger py-2">{err}</div> : null}

        <div className="row g-3 mb-24">
          {stat("Total scans and clicks", loading ? "…" : total, "mdi:cursor-default-click-outline")}
          {stat("iPhone and iPad", loading ? "…" : byPlatform.ios || 0, "mdi:apple")}
          {stat("Android", loading ? "…" : byPlatform.android || 0, "mdi:android")}
          {stat("Desktop and other", loading ? "…" : byPlatform.other || 0, "mdi:monitor")}
        </div>

        <h6 className="text-md fw-semibold mb-12">By channel</h6>
        <div className="table-responsive">
          <table className="table bordered-table mb-0">
            <thead>
              <tr>
                <th>Channel</th>
                <th className="text-end">Total</th>
                <th className="text-end">iPhone / iPad</th>
                <th className="text-end">Android</th>
                <th className="text-end">Desktop</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="text-center text-secondary-light py-24">
                    Loading…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center text-secondary-light py-24">
                    No scans yet in this window. Counting starts the moment the
                    first person scans a code.
                  </td>
                </tr>
              ) : (
                rows.map((r) => {
                  const known = CHANNELS.find((c) => c.src === r.src);
                  return (
                    <tr key={r.src}>
                      <td>
                        <span className="fw-semibold">{known ? known.label : r.src}</span>
                        {r.src !== "(none)" ? (
                          <span className="text-sm text-secondary-light ms-2">?src={r.src}</span>
                        ) : (
                          <span className="text-sm text-secondary-light ms-2">
                            no tag (link used without ?src)
                          </span>
                        )}
                      </td>
                      <td className="text-end fw-semibold">{r.total}</td>
                      <td className="text-end">{r.ios}</td>
                      <td className="text-end">{r.android}</td>
                      <td className="text-end">{r.other}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <h6 className="text-md fw-semibold mt-32 mb-12">Your links</h6>
        <p className="text-sm text-secondary-light mb-12">
          Use the matching link wherever a channel lives, and it will appear in
          the table above. Any new tag works: add ?src=your_tag to the end.
        </p>
        <div className="d-flex flex-column gap-2">
          {CHANNELS.map((c) => {
            const url = `https://curiouzz.com/get?src=${c.src}`;
            return (
              <div
                key={c.src}
                className="d-flex align-items-center justify-content-between p-12 radius-8 bg-neutral-50"
              >
                <div>
                  <div className="fw-semibold text-sm">{c.label}</div>
                  <code className="text-sm text-secondary-light">{url}</code>
                </div>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
                  onClick={() => copy(url, c.src)}
                >
                  <Icon icon={copied === c.src ? "mdi:check" : "mdi:content-copy"} />
                  {copied === c.src ? "Copied" : "Copy"}
                </button>
              </div>
            );
          })}
        </div>

        <p className="text-sm text-secondary-light mt-24 mb-0">
          These are scans and clicks, not installs. Android carries the campaign
          through to the install, so Play Console can close that loop. The App
          Store accepts no install referrer, so an iPhone install cannot be
          traced back to a single scan.
        </p>
      </div>
    </div>
  );
};

export default DownloadHitsLayer;
